import { useState, useEffect, useCallback, useRef } from 'react';
import { getPhotos, uploadPhoto, deletePhoto, updatePhotoTitle } from '../../lib/api/adminApi';

const CATEGORIES = ['Plomberie', 'Chauffage', 'Climatisation', 'Salle de bain', 'Pompe à chaleur', 'Illustration RGE'];

const AdminPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [dragover, setDragover] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileInputRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await getPhotos();
      setPhotos(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = photos.filter(p => p.category === activeCategory);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', activeCategory);
      fd.append('title', title || file.name.replace(/\.[^.]+$/, ''));
      const { data } = await uploadPhoto(fd);
      setPhotos(prev => [data, ...prev]);
      setTitle('');
    } catch (err) {
      alert(err.response?.data?.message ?? 'Erreur lors de l\'upload.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) handleUpload(e.target.files[0]);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  };

  const handleDelete = async (id) => {
    await deletePhoto(id);
    setPhotos(prev => prev.filter(p => p.id !== id));
    setConfirmDelete(null);
  };

  const handleTitleSave = async (id, newTitle) => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    await updatePhotoTitle(id, trimmed);
    setPhotos(prev => prev.map(p => p.id === id ? { ...p, title: trimmed } : p));
  };

  if (loading) return <div className="admin-spinner">Chargement...</div>;

  return (
    <>
      <div className="admin-page-title">Gestion des photos</div>
      <div className="admin-page-sub">Ajoutez et gérez les photos par catégorie de service.</div>

      <div className="admin-category-tabs">
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            className={`admin-cat-tab${activeCategory === cat ? ' active' : ''}`}
            onClick={() => setActiveCategory(cat)}
          >
            {cat} <span style={{ opacity: 0.7, marginLeft: 4 }}>({photos.filter(p => p.category === cat).length})</span>
          </button>
        ))}
      </div>

      {/* Upload zone */}
      <div className="admin-card" style={{ marginBottom: 24, padding: 20 }}>
        <div className="admin-form-group" style={{ marginBottom: 12 }}>
          <label className="admin-form-label">Titre de la photo (optionnel)</label>
          <input
            className="admin-form-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder={`Photo ${activeCategory}...`}
          />
        </div>
        <div
          className={`admin-upload-zone${dragover ? ' dragover' : ''}`}
          onClick={() => fileInputRef.current.click()}
          onDragOver={e => { e.preventDefault(); setDragover(true); }}
          onDragLeave={() => setDragover(false)}
          onDrop={handleDrop}
        >
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileChange} />
          {uploading ? (
            <div className="admin-upload-icon">⏳</div>
          ) : (
            <div className="admin-upload-icon">📸</div>
          )}
          <div className="admin-upload-text">
            {uploading ? 'Upload en cours...' : 'Cliquez ou glissez une photo ici'}
          </div>
          <div className="admin-upload-sub">JPG, PNG, WebP — 15 Mo max — Catégorie : <strong>{activeCategory}</strong></div>
        </div>
      </div>

      {/* Photos grid */}
      {filtered.length === 0 ? (
        <div className="admin-empty" style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
          Aucune photo pour la catégorie <strong>{activeCategory}</strong> — ajoutez-en une ci-dessus.
        </div>
      ) : (
        <div className="admin-photos-grid">
          {filtered.map(p => (
            <div key={p.id} className="admin-photo-card">
              <img src={p.url} alt={p.title ?? p.fileName} className="admin-photo-img" />
              <div className="admin-photo-info">
                <input
                  className="admin-photo-title-input"
                  defaultValue={p.title ?? p.fileName}
                  onBlur={e => handleTitleSave(p.id, e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                  title="Cliquez pour modifier le titre"
                />
                <button className="admin-photo-delete" onClick={() => setConfirmDelete(p)}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL SUPPRIMER */}
      {confirmDelete && (
        <div className="admin-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="admin-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <div className="admin-modal-title">Supprimer cette photo ?</div>
              <button className="admin-modal-close" onClick={() => setConfirmDelete(null)}>✕</button>
            </div>
            <div className="admin-modal-body">
              <p style={{ color: '#374151', fontSize: '0.9rem' }}>
                Supprimer <strong>"{confirmDelete.title ?? confirmDelete.fileName}"</strong> ? Cette action est irréversible.
              </p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-secondary" onClick={() => setConfirmDelete(null)}>Annuler</button>
              <button className="admin-btn admin-btn-danger" onClick={() => handleDelete(confirmDelete.id)}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPhotos;
